import { useCallback, useEffect, useRef, useState } from 'react';

import { Button } from 'basic-components';
import { ArrowLeft, ArrowRight } from 'basic-components/icons';

import classNames from 'classnames';
import './FilterCarousel.scss';

const EMPTY_FUN = () => {}
const INITIAL_SCROLL_STATE = { hasOverflow: false, canScrollLeft: false, canScrollRight: false }

const FilterCarousel = ({ activeFilterCount = 0, children, className = undefined, minActiveFilterCount = 2, onClearAll = EMPTY_FUN, onClickCapture }) => {
  const viewportRef = useRef(null),
        contentRef = useRef(null),
        scrollOffsetRef = useRef(0),
        [scrollOffset, setScrollOffset] = useState(0),
        [scrollState, setScrollState] = useState(INITIAL_SCROLL_STATE),
        updateScrollState = useCallback(() => {
          const viewport = viewportRef.current,
                content = contentRef.current
          if(!viewport || !content) {
            return
          }

          const { clientWidth } = viewport,
                scrollWidth = content.scrollWidth,
                maxScrollOffset = Math.max(scrollWidth - clientWidth, 0),
                currentScrollOffset = Math.min(scrollOffsetRef.current, maxScrollOffset),
                hasOverflow = scrollWidth > clientWidth + 1,
                nextScrollState = {
                  hasOverflow,
                  canScrollLeft: hasOverflow && currentScrollOffset > 1,
                  canScrollRight: hasOverflow && currentScrollOffset + clientWidth < scrollWidth - 1
                }

          if(currentScrollOffset !== scrollOffsetRef.current) {
            scrollOffsetRef.current = currentScrollOffset
            setScrollOffset(currentScrollOffset)
          }
          setScrollState(currentState => Object.keys(nextScrollState).every(key => currentState[key] === nextScrollState[key]) ? currentState : nextScrollState)
        }, []),
        scroll = useCallback(direction => {
          const viewport = viewportRef.current,
                content = contentRef.current
          if(!viewport || !content) {
            return
          }

          const distance = Math.max(viewport.clientWidth - 64, 120),
                maxScrollOffset = Math.max(content.scrollWidth - viewport.clientWidth, 0),
                nextScrollOffset = Math.min(maxScrollOffset, Math.max(0, scrollOffsetRef.current + direction * distance))
          scrollOffsetRef.current = nextScrollOffset
          setScrollOffset(nextScrollOffset)
          updateScrollState()
        }, [updateScrollState])

  useEffect(() => {
    const viewport = viewportRef.current,
          content = contentRef.current,
          resizeObserver = typeof ResizeObserver !== "undefined" ? new ResizeObserver(updateScrollState) : undefined

    updateScrollState()
    resizeObserver?.observe(viewport)
    if(content) {
      resizeObserver?.observe(content)
    }
    window.addEventListener("resize", updateScrollState)

    return () => {
      resizeObserver?.disconnect()
      window.removeEventListener("resize", updateScrollState)
    }
  }, [activeFilterCount, children, updateScrollState])

  return (
    <div
        className={classNames("ng-picker-filter-carousel", className)}
        onClickCapture={onClickCapture}
    >
      <div className="ng-picker-filter-carousel__track">
        <div
            className="ng-picker-filter-carousel__viewport"
            data-testid="filter-carousel-viewport"
            ref={viewportRef}
        >
          <div
              className="ng-picker-filter-carousel__content"
              ref={contentRef}
              style={{ transform: `translateX(-${scrollOffset}px)` }}
          >
            {children}
            {activeFilterCount >= minActiveFilterCount && (
              <div className="ng-picker-filter-carousel__clear-all">
                <Button
                    data-testid="clear-all-filters"
                    onClick={onClearAll}
                    size="small"
                    type="button"
                    variant="text"
                >
                  Clear all
                </Button>
              </div>
            )}
          </div>
        </div>
        {scrollState.canScrollLeft && (
          <button
              aria-label="Scroll filters left"
              className="ng-picker-filter-carousel__arrow ng-picker-filter-carousel__arrow--left"
              data-testid="filter-carousel-left"
              onClick={() => scroll(-1)}
              type="button"
          >
            <ArrowLeft
                color="grey-dark"
                height={16}
                isWithoutBorder
                width={16}
            />
          </button>
        )}
        {scrollState.canScrollRight && (
          <button
              aria-label="Scroll filters right"
              className="ng-picker-filter-carousel__arrow ng-picker-filter-carousel__arrow--right"
              data-testid="filter-carousel-right"
              onClick={() => scroll(1)}
              type="button"
          >
            <ArrowRight
                color="grey-dark"
                height={16}
                isWithoutBorder
                width={16}
            />
          </button>
        )}
      </div>
    </div>
  )
}

export default FilterCarousel
